import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, CheckCircle2, Loader2, Stethoscope,
  User, Phone, Building2, GraduationCap, Home, Plus, FileText, ClipboardList, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/ImageUpload";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import formatDoctorName from "@/lib/names";

const EMPTY = {
  manager_name: "", manager_mobile: "", manager_address: "", headquarter: "",
  doctor_name: "", qualification: "",
  doctor_photo: "", additional_requirements: "",
};

// Manager details are the same on every submission, so they are stored on the manager's
// account and copied into each new form; they can still be edited per submission.
const profileDefaults = (user) => ({
  manager_name: user?.name || "",
  manager_mobile: user?.mobile || "",
  manager_address: user?.address || "",
  headquarter: user?.headquarter || "",
});

const STATUS_STYLES = {
  Pending: "bg-amber-50 text-amber-600",
  Scheduled: "bg-blue-50 text-blue-600",
  Decorated: "bg-emerald-50 text-emerald-600",
};

export default function MRDashboard() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [subs, setSubs] = useState([]);
  const [view, setView] = useState("form"); // form | list | profile
  const [profile, setProfile] = useState({
    manager_name: "", manager_mobile: "", manager_address: "", headquarter: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [submissionsOpen, setSubmissionsOpen] = useState(true);
  const [closedMessage, setClosedMessage] = useState(
    "Submissions are now closed. Thank you for your entries — the Blue Cross team is arranging the clinic setups.",
  );

  const loadSubs = () => api.get("/submissions/me").then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => { loadSubs(); }, []);

  // The drive can be closed from the backend; fall back to open so a failed
  // config call never locks a manager out of a drive that is still running.
  useEffect(() => {
    api.get("/config")
      .then(({ data }) => {
        setSubmissionsOpen(data.submissions_open !== false);
        if (data.submissions_closed_message) setClosedMessage(data.submissions_closed_message);
        // Stay on the "form" view when closed: it renders the notice, so a manager
        // is told why there is nothing to fill in rather than silently losing the tab.
      })
      .catch(() => {});
  }, []);

  // Prefill the manager block once the account loads, without clobbering anything typed.
  useEffect(() => {
    if (!user) return;
    const defaults = profileDefaults(user);
    setForm((f) => {
      const filled = { ...f };
      for (const [k, v] of Object.entries(defaults)) if (!filled[k]) filled[k] = v;
      return filled;
    });
    setProfile(defaults);
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    // The uploader uses a hidden file input, so `required` can't cover this.
    if (!form.doctor_photo) {
      toast.error("Please upload the doctor photo.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/submissions", form);
      toast.success("Clinic details submitted!");
      setDone(true);
      setForm({ ...EMPTY, ...profile });
      loadSubs();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => { logout(); navigate("/"); };

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/auth/me", {
        name: profile.manager_name,
        mobile: profile.manager_mobile,
        address: profile.manager_address,
        headquarter: profile.headquarter,
      });
      setUser(data);
      // Push the saved values into the open form so the next submission uses them.
      setForm((f) => ({ ...f, ...profile }));
      toast.success("Your details are saved and will prefill each new form.");
      setView("form");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save details");
    } finally {
      setSavingProfile(false);
    }
  };

  const setProfileKey = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const profileField = (icon, label, key, opts = {}) => (
    <div className="space-y-2">
      <Label htmlFor={`profile-${key}`} className="flex items-center gap-2 text-slate-700">
        {icon} {label}
      </Label>
      {opts.textarea ? (
        <Textarea id={`profile-${key}`} data-testid={`profile-field-${key}`} value={profile[key]}
          onChange={setProfileKey(key)} placeholder={opts.ph} className="bg-slate-50 min-h-[88px]" />
      ) : (
        <Input id={`profile-${key}`} data-testid={`profile-field-${key}`} type={opts.type || "text"}
          value={profile[key]} onChange={setProfileKey(key)} placeholder={opts.ph} className="h-12 bg-slate-50" />
      )}
    </div>
  );

  const field = (icon, label, key, opts = {}) => {
    const required = opts.optional !== true;
    return (
      <div className="space-y-2">
        <Label htmlFor={key} className="flex items-center gap-2 text-slate-700">
          {icon} {label}
          {!required && <span className="text-slate-400 text-xs font-normal">(optional)</span>}
        </Label>
        {opts.textarea ? (
          <Textarea id={key} data-testid={`field-${key}`} value={form[key]} onChange={set(key)}
            placeholder={opts.ph} className="bg-slate-50 min-h-[88px]" required={required} />
        ) : (
          <Input id={key} data-testid={`field-${key}`} type={opts.type || "text"} value={form[key]}
            onChange={set(key)} placeholder={opts.ph} className="h-12 bg-slate-50"
            inputMode={opts.inputMode} maxLength={opts.maxLength} required={required} />
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-heading font-bold text-slate-900 leading-none">Blue Cross</p>
              <p className="text-xs text-slate-500">World Diabetes Day</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-semibold text-slate-800">{user?.name || user?.emp_code}</p>
              <p className="text-xs text-slate-500 font-mono">{user?.emp_code}</p>
            </div>
            <Button data-testid="mr-logout" onClick={doLogout} variant="outline" size="sm" className="rounded-lg h-11 px-3">
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex sm:inline-flex w-full sm:w-auto p-1 rounded-xl bg-slate-100 border border-slate-200">
          {submissionsOpen && (
            <button data-testid="tab-form" onClick={() => { setView("form"); setDone(false); }}
              className={`flex-1 sm:flex-none whitespace-nowrap px-3 sm:px-4 h-11 rounded-lg text-sm font-semibold transition-colors ${view === "form" ? "bg-white text-[#0B5351] shadow-sm" : "text-slate-500"}`}>
              <Plus className="h-4 w-4 inline mr-1.5" /> New
            </button>
          )}
          <button data-testid="tab-list" onClick={() => setView("list")}
            className={`flex-1 sm:flex-none whitespace-nowrap px-3 sm:px-4 h-11 rounded-lg text-sm font-semibold transition-colors ${view === "list" ? "bg-white text-[#0B5351] shadow-sm" : "text-slate-500"}`}>
            <FileText className="h-4 w-4 inline mr-1.5" /> Submissions ({subs.length})
          </button>
          <button data-testid="tab-profile" onClick={() => setView("profile")}
            className={`flex-1 sm:flex-none whitespace-nowrap px-3 sm:px-4 h-11 rounded-lg text-sm font-semibold transition-colors ${view === "profile" ? "bg-white text-[#0B5351] shadow-sm" : "text-slate-500"}`}>
            <User className="h-4 w-4 inline mr-1.5" /> Profile
          </button>
        </div>
      </div>

      <main className="max-w-lg mx-auto px-4 sm:px-6 py-8">
        {!submissionsOpen && view === "form" ? (
          <div className="animate-fade-up" data-testid="submissions-closed">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_24px_rgba(15,23,42,0.06)] p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-amber-50 grid place-items-center mx-auto mb-5">
                <Lock className="h-8 w-8 text-amber-500" />
              </div>
              <h1 className="font-heading text-2xl font-black tracking-tight text-slate-900 mb-3">
                Submissions Closed
              </h1>
              <p className="text-slate-500 leading-relaxed">{closedMessage}</p>
              <Button data-testid="closed-view-submissions" onClick={() => setView("list")}
                className="mt-7 rounded-xl bg-[#0B5351] hover:bg-[#094241] h-12 px-8">
                <FileText className="h-4 w-4 mr-2" /> View My Submissions ({subs.length})
              </Button>
            </div>
          </div>
        ) : view === "form" && done ? (
          <div className="text-center py-12 animate-fade-up" data-testid="submission-success">
            <div className="h-20 w-20 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-6">
              <CheckCircle2 className="h-11 w-11 text-emerald-500" />
            </div>
            <h2 className="font-heading text-2xl font-black tracking-tight text-slate-900">Submitted!</h2>
            <p className="text-slate-500 mt-2 max-w-xs mx-auto">
              The Blue Cross team will arrange the clinic setup. Thank you.
            </p>
            <Button data-testid="add-another" onClick={() => setDone(false)}
              className="mt-8 rounded-xl bg-[#0B5351] hover:bg-[#094241] h-12 px-8">
              <Plus className="h-4 w-4 mr-2" /> Add Another Clinic
            </Button>
          </div>
        ) : view === "form" ? (
          <div className="animate-fade-up">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Clinic Details</p>
            <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 mb-1">Awareness Setup Request</h1>
            <p className="text-slate-500 mb-8">Fill the details collected from the clinic.</p>

            <form onSubmit={submit} className="space-y-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
              {field(<Stethoscope className="h-4 w-4 text-[#0B5351]" />, "Doctor's Name", "doctor_name", { ph: "Dr. …" })}
              {field(<GraduationCap className="h-4 w-4 text-[#0B5351]" />, "Qualification", "qualification", { ph: "e.g. MBBS, MD" })}

              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-slate-700">
                  <Stethoscope className="h-4 w-4 text-[#0B5351]" /> Doctor Photo
                </Label>
                <ImageUpload label="Doctor Photo" testid="doctor-photo" value={form.doctor_photo}
                  onChange={(p) => setForm((f) => ({ ...f, doctor_photo: p }))} />
              </div>

              {field(<ClipboardList className="h-4 w-4 text-[#0B5351]" />, "Any Additional Request / Requirements", "additional_requirements", { ph: "e.g. Number of sets required, or any other requirement", textarea: true, optional: true })}

              <div className="rounded-xl bg-slate-50/70 border border-slate-200 p-4 sm:p-5 space-y-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-heading font-bold text-slate-900 text-sm">Your details</p>
                    <p className="text-xs text-slate-500">Prefilled from your profile — edit here if this clinic differs.</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" data-testid="edit-profile-link"
                    onClick={() => setView("profile")} className="rounded-lg shrink-0 h-11 px-3">
                    Edit defaults
                  </Button>
                </div>
                {field(<User className="h-4 w-4 text-[#0B5351]" />, "Manager Name", "manager_name", { ph: "Full name" })}
                {field(<Phone className="h-4 w-4 text-[#0B5351]" />, "Manager Mobile", "manager_mobile", { ph: "10-digit number", type: "tel" })}
                {field(<Home className="h-4 w-4 text-[#0B5351]" />, "Manager Address", "manager_address", { ph: "Your address", textarea: true, optional: true })}
                {field(<Building2 className="h-4 w-4 text-[#0B5351]" />, "Head Quarter", "headquarter", { ph: "e.g. Mumbai" })}
              </div>

              <Button type="submit" data-testid="mr-form-submit" disabled={loading}
                className="w-full h-12 rounded-xl bg-[#E63946] hover:bg-[#D62828] text-white font-semibold">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit Clinic Details"}
              </Button>
            </form>
          </div>
        ) : view === "profile" ? (
          <div className="animate-fade-up" data-testid="mr-profile">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">My Profile</p>
            <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900 mb-1">Your default details</h1>
            <p className="text-slate-500 mb-8">
              Saved once and prefilled into every new submission, so you don't retype them each time.
            </p>

            <form onSubmit={saveProfile} className="space-y-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-[0_8px_24px_rgba(15,23,42,0.06)]">
              {profileField(<User className="h-4 w-4 text-[#0B5351]" />, "Manager Name", "manager_name", { ph: "Full name" })}
              {profileField(<Phone className="h-4 w-4 text-[#0B5351]" />, "Manager Mobile", "manager_mobile", { ph: "10-digit number", type: "tel" })}
              {profileField(<Home className="h-4 w-4 text-[#0B5351]" />, "Manager Address", "manager_address", { ph: "Your address", textarea: true })}
              {profileField(<Building2 className="h-4 w-4 text-[#0B5351]" />, "Head Quarter", "headquarter", { ph: "e.g. Mumbai" })}

              <Button type="submit" data-testid="profile-save" disabled={savingProfile}
                className="w-full h-12 rounded-xl bg-[#0B5351] hover:bg-[#094241] text-white font-semibold">
                {savingProfile ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save My Details"}
              </Button>
            </form>
          </div>
        ) : (
          <div className="animate-fade-up space-y-4" data-testid="mr-submissions-list">
            <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-900">My Submissions</h1>
            {subs.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
                <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No submissions yet.</p>
              </div>
            ) : (
              subs.map((s) => (
                <div key={s.id} className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-heading font-bold text-slate-900">{formatDoctorName(s.doctor_name)}</p>
                      <p className="text-sm text-slate-500">{s.qualification} · {s.headquarter}</p>
                    </div>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[s.status] || STATUS_STYLES.Pending}`}>{s.status || "Pending"}</span>
                  </div>
                  {s.additional_requirements ? (
                    <p className="text-sm text-slate-500 mt-3 flex items-start gap-2 whitespace-pre-line"><ClipboardList className="h-4 w-4 mt-0.5 shrink-0" /> {s.additional_requirements}</p>
                  ) : null}
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
